import React, { useState, useEffect } from "react";
import { getImage } from "gatsby-plugin-image";
import { graphql } from "gatsby";
import Seo from "../components/seo";
import CommunityHeader from "../components/communityHeader";
import SlugWrapCommunityFooter from "../components/slugWrapCommunityFooter";
import ContactForm from "../components/contactForm";
import {
  hoverTitle,
  hoverTitleWrap,
  fontSizeFive,
  fontColorBlue,
  fontColorDark,
} from "../components/shared.module.scss";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { v4 as uuidv4 } from "uuid";
import UnitDisplayCards from "../components/unitDisplayCards";
import { getYardiPropId } from "../utils/propertyMapUtil";
import { extractCityAndState } from "../utils/helperFunctions";

const FloorplanPage = ({ data }) => {
  const [avCount, setAvCount] = useState(0);
  const [cid, setCid] = useState(data.contentfulCommunity.rmpropid);
  const [units, setUnits] = useState([]);
  const [floorplanResults, setFloorplanResults] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [deals, setDeals] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDollhouse, setShowDollhouse] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    //Check if we are on a mobile size screen
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const handleShowContactForm = () => {
    setShowContactForm(!showContactForm);
  };

  useEffect(() => {
    let fetchPromises = [];

    const yardi_cid = getYardiPropId(data.contentfulCommunity.rmpropid);

    // 1) Fetch units
    fetchPromises.push(
      fetch(
        `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${yardi_cid}`
      )
        .then((response) => response.json())
        .then((resultData) => parseUnitsData(resultData))
    );

    // Extra fetch calls for certain communities
    if (yardi_cid == "beachfl1") {
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl2`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl3`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
    }
    if (yardi_cid === "sc1") {
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc2`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc3`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
    }
    if (yardi_cid === "golfla12") {
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla03`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
    }
    if (yardi_cid === "willfla1") {
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla2`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
    }
    if (yardi_cid === "mcdonald") {
      fetchPromises.push(
        fetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=gleno2`
        )
          .then((response) => response.json())
          .then((resultData) => parseUnitsData(resultData))
      );
    }

    // 2) Fetch deals
    const fetchDeals = fetch(
      "https://redisut.mcklab.com/index.php?action=getDeals"
    )
      .then((response) => response.json())
      .then((resultData) => (resultData.res ? JSON.parse(resultData.res) : {}));

    fetchPromises.push(fetchDeals);

    // Resolve all
    Promise.all(fetchPromises)
      .then((results) => {
        const dealsResult = results.pop(); // deals are last
        let unitsResult = results.flat(); // flatten all unit data
        // Community-specific hacks or renaming
        if (cid == "401910") {
          unitsResult.forEach((n) => {
            switch (n.floorplanName) {
              case "700sf ":
                n.floorplanName = "Iris Meadow";
                break;
              case "570sf ":
                n.floorplanName = "Purple Milkweed";
                break;
              default:
                break;
            }
          });
        } else if (cid == "401810") {
          unitsResult.forEach((n) => {
            n.floorplanName = "West Mesa";
          });
        } else if (cid == "401830") {
          //Willow Flats
          unitsResult.forEach((n) => {
            if (n.amenities.includes("Sbd x 1ba")) {
              n.floorplanName = "Aloe";
            }
            if (n.amenities.includes("2bd x 1ba")) {
              n.floorplanName = "Morning Glory";
            }
            if (n.amenities.includes("1bd x 1ba")) {
              n.floorplanName = "Canary";
            }
          });
        } else if (yardi_cid == "beachfl1") {
          unitsResult.forEach((n) => {
            if (n.amenities.includes("2bd x 1ba - 900sf")) {
              n.floorplanName = "Boca";
            }
            if (n.amenities.includes("2bd x 2ba")) {
              n.floorplanName = "Warwick Long Bay";
            }
            if (n.amenities.includes("1bd x 1ba")) {
              n.floorplanName = "Sanibel";
            }
            if (n.amenities.includes("Studio x 1ba")) {
              n.floorplanName = "Captiva";
            }
            if (n.amenities.includes("Pensacola")) {
              n.floorplanName = "Pensacola";
            }
          });
        } else if (yardi_cid == "brooklyn") {
          const unitsResultClone = unitsResult.map((item) => {
            return {
              ...item,
              floorplanName: item.floorplanName.replace(
                "The Henderson",
                "The Darcy"
              ),
            };
          });
          try {
            unitsResult = unitsResult.concat(unitsResultClone);
          } catch (error) {
            console.error("Error during concatenation:", error);
          }
        } else if (yardi_cid == "capri") {
          // Filter out non-market rent units
          /*unitsResult = unitsResult.filter((n) => {
            return (
              n.amenities &&
              (n.amenities.includes("Market") || n.amenities.includes("Marke"))
            );
          });
          // Remove "Market" from floorplan names
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("Market")) {
              n.floorplanName = n.floorplanName.replace("Market", "");
            }
            if (n.floorplanName.includes("Marke")) {
              n.floorplanName = n.floorplanName.replace("Marke", "");
            }
          });
          */
          unitsResult.forEach((n) => {
            if (n.amenities.includes("Punta Carena")) {
              const pos = n.extra.toLowerCase();
              if (pos.includes("renovated") || pos.includes("upgraded")) {
                n.floorplanName = n.floorplanName.replace("Punta Carena", "Punta Carena+");
                n.amenities = n.amenities.replace("Punta Carena", "Punta Carena+");
              }
            }
          });


        } else if (yardi_cid == "coconut") {
          unitsResult.forEach((n) => {
            //if(n.unit == '4566-G'){
            //  console.log('found it',n);
            //}
            if (n.floorplanName == "Estereo bay") {
              n.floorplanName = "Estereo Bay";
            }
            if (n.floorplanName == "Crystal River") {
              n.floorplanName = "Alafia River";
            }
            if (n.floorplanName.includes("Plus")) {
              n.floorplanName = n.floorplanName.replace(" Plus", "+");
            }
          });
        } else if (yardi_cid == "harbor") {
          unitsResult.forEach((n) => {
            if (n.floorplanName == "barber") {
              n.floorplanName = "Barber";
            }
          });
        } else if (yardi_cid == "evergree") {
          unitsResult.forEach((n) => {
            if (n.floorplanName == "Angell") {
              n.floorplanName = "Bryant";
            }
            if (n.floorplanName == "Burns Park") {
              n.floorplanName = "Clague";
            }
            if (n.floorplanName == "Kerrytown") {
              n.floorplanName = "Georgetown";
            }
          });
        } else if (yardi_cid == "mcdonald") {
          unitsResult.forEach((n) => {
            if (n.floorplanName == "Birch") {
              n.floorplanName = "Carriage House";
            }
          });
          // --- Start Swapping Logic ---
          let unit_05 = null;
          let unit_07 = null;

          unitsResult.forEach((n) => {
            if (n.unit === "2175-05") {
              unit_05 = n;
            } else if (n.unit === "2175-07") {
              unit_07 = n;
            }
          });

          if (unit_05 && unit_07) {
            // Swap amenities
            const tempAmenities = unit_05.amenities;
            unit_05.amenities = unit_07.amenities;
            unit_07.amenities = tempAmenities;

            // Swap floorplanName
            const tempFloorplanName = unit_05.floorplanName;
            unit_05.floorplanName = unit_07.floorplanName;
            unit_07.floorplanName = tempFloorplanName;
          }
          // --- End Swapping Logic ---
        } else if (yardi_cid == "tracross") {
          unitsResult.forEach((n) => {
            if (n.floorplanName === "The bagley") {
              n.floorplanName = "The Bagley";
            }
          });
        } else if (yardi_cid == "sc1") {
          unitsResult.forEach((n) => {
            if (n.floorplanName === "The Grand") {
              n.floorplanName = "The Gaston & The Grand";
            }
          });
        } else if (cid == "400410") {
          unitsResult.forEach((n) => {
            if (n.floorplanName === "Parc") {
              n.floorplanName = "The Conroy";
            }
          });
        } else if (yardi_cid == "manflats") {
          unitsResult.forEach((n) => {
            if (n.unit.trim() == "1820-01") {
              n.floorplanName = "The Fletcher";
            }
          });
        } else if (yardi_cid == "medical") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("barton")) {
              n.floorplanName = n.floorplanName.replace("barton", "Barton");
            }
          });
        } else if (yardi_cid == "spruce") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("basquiat")) {
              n.floorplanName = n.floorplanName.replace("basquiat", "Basquiat");
            }
          });
        } else if (yardi_cid == "indigo") {
          unitsResult.forEach((n) => {
            if (n.floorplanName === "College") {
              n.floorplanName = "Harmony";
            }
          });
        } else if (yardi_cid == "sapphire") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("Plus")) {
              n.floorplanName = n.floorplanName.replace(" Plus", "+");
            }
            if (n.floorplanName.includes("Signature")) {
              n.floorplanName = n.floorplanName.replace(" Signature", "+");
            }
          });
        } else if (yardi_cid == "tracourt") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("Plus")) {
              n.floorplanName = n.floorplanName.replace(" Plus", "+");
            }
          });
        } else if (yardi_cid == "westwood") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("Plus")) {
              n.floorplanName = n.floorplanName.replace(" Plus", "+");
            }
          });
        } else if (yardi_cid == "azure") {
          unitsResult.forEach((n) => {
            if (n.floorplanName.includes("Plus")) {
              n.floorplanName = n.floorplanName.replace(" Plus", "+");
            }
          });
        } else if (yardi_cid == "meadow") {
          unitsResult.forEach((n) => {
            if (n.floorplanName == "The Meadowlark" && n.extra.includes("Top")) {
              n.floorplanName = "The Cardinal";
            }
            if (n.floorplanName == "The Starling" && n.extra.includes("Ground")) {
              n.floorplanName = "Blue Heron";
            }
          });
        }

        const noDollhouseCommunities = [401810, 401910, 401830, 401520];
        if (noDollhouseCommunities.includes(cid)) {
          setShowDollhouse(false);
        }

        // Clean up deals
        dealsResult.forEach((r) => {
          if (r.sdesc == "2bd x 1.5ba - 1") {
            r.sdesc = "2bd x 1.5ba - 1000sf - Chatham";
            r.sdesc1 = r.scode1;
          }
          if (r.sdesc.includes("barton")) {
            r.sdesc = r.sdesc.replace("barton", "Barton");
          }
          if (r.sdesc.includes("College")) {
            r.sdesc = r.sdesc.replace("College", "Harmony");
          }
          if (r.sdesc.includes("barber")) {
            r.sdesc = r.sdesc.replace("barber", "Barber");
          }
          if (r.sdesc1 == "$1") {
            r.sdesc1 = "$1,000 Off First Month's Rent";
          }
          if (r.sdesc.includes("Angell")) {
            r.sdesc = r.sdesc.replace("Angell", "Bryant");
          }
          if (r.sdesc.includes("Burns Park")) {
            r.sdesc = r.sdesc.replace("Burns Park", "Clague");
          }
        });
        //console.log('dealsResult',dealsResult);
        //console.log('unitsResult',unitsResult);

        setDeals(dealsResult);
        setUnits(unitsResult);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading data:", error);
        setLoading(false);
      });
  }, [data.contentfulCommunity.rmpropid]);

  const parseUnitsData = (data) => {
    const resData = JSON.parse(data.res);
    const avnow = ["Vacant Unrented Not Ready", "Vacant Unrented Ready"];
    return resData.map((unit) => ({
      unit: unit.unit,
      isVacant: avnow.includes(unit.status),
      status: unit.status,
      price: unit.rent1,
      extra: unit.position,
      amenities: unit.amenities,
      moveOutDate: unit.date1,
      otherDate: unit.date2,
      floorplanName: unit.amenities.split(" - ").pop(),
    }));
  };

  useEffect(() => {
    const findFloorplanUnits = (unitsList, floorplanName) => {
      // Deal with trailing plus sign on floorplan names
      if (floorplanName.endsWith("+")) {
        floorplanName =
          data.contentfulCommunity.rmpropid == "401460" ||
          data.contentfulCommunity.rmpropid == "401300" ||
          data.contentfulCommunity.rmpropid == "406470" ||
          data.contentfulCommunity.rmpropid == "400550" ||
          data.contentfulCommunity.rmpropid == "400220" ||
          data.contentfulCommunity.rmpropid == "401730"
            ? floorplanName
            : floorplanName.slice(0, -1);
      }

      //console.log('floorplanName',floorplanName);

      const filteredUnits = unitsList.filter(
        (unit) => unit.floorplanName.trim() === floorplanName.trim()
      );
      //console.log('filteredUnits',filteredUnits);
      const vacantUnits = filteredUnits.filter((unit) => unit.isVacant);
      //console.log('vacantUnits',vacantUnits);
      const avsoon = ["Notice Unrented"];
      const noticeUnits = filteredUnits.filter((unit) =>
        avsoon.includes(unit.status)
      );

      let combinedUnits = [
        ...vacantUnits,
        ...noticeUnits.sort(
          (a, b) => new Date(a.moveOutDate) - new Date(b.moveOutDate)
        ),
      ];
      //console.log('combinedUnits',combinedUnits);

      function hasDeal(unit) {
        //console.log('unit',unit);
        const unitTrimmed = unit.unit.trim();
        const currentFpName = unit.floorplanName;
        for (let i = 0; i < deals.length; i++) {
          const deal = deals[i];
          //console.log('deal',deal);
          // Normalize deal description: replace " Plus" with "+"
          const normalizedSdesc = deal.sdesc.replace(/ Plus/g, '+');
          if (
            deal.unit === unitTrimmed &&
            normalizedSdesc.includes(currentFpName)
          ) {
            //console.log('deal',deal);
            //console.log('unit',unit);
            return true;
          }
        }

        return false;
      }

      let unitsWithDeals = combinedUnits.filter(hasDeal);
      //console.log('unitsWithDeals',unitsWithDeals);
      unitsWithDeals = unitsWithDeals.sort(
        (a, b) => new Date(a.moveOutDate) - new Date(b.moveOutDate)
      );
      //console.log('unitsWithDeals',unitsWithDeals);
      let unitsWithoutDeals = combinedUnits.filter((unit) => !hasDeal(unit));
      let sortedUnits = [...unitsWithDeals, ...unitsWithoutDeals];

      // Only return top 3
      sortedUnits = sortedUnits.slice(0, 3);

      let avc = combinedUnits.length;
      if (avc === 0) {
        return { hasVacantUnits: false };
      }

      setAvCount(avc);

      // Optional override logic
      if (
        data.contentfulFloorplans.unitOrderOverride &&
        data.contentfulFloorplans.unitOrderOverride.length > 0
      ) {
        const Ounits = data.contentfulFloorplans.unitOrderOverride;
        // Move them in reverse order so the first item is moved last
        for (let i = 2; i >= 0; i--) {
          if (
            Ounits[i] &&
            Ounits[i] !== "na" &&
            Ounits[i] !== "none" &&
            Ounits[i] !== "default"
          ) {
            const index = sortedUnits.findIndex((unit) => unit.ID === Ounits[i]);
            if (index !== -1) {
              const [unitToMove] = sortedUnits.splice(index, 1);
              sortedUnits.unshift(unitToMove);
            }
          }
        }
      }

      const lowestPricedUnit = sortedUnits[0];
      const middlePricedUnit = sortedUnits.length > 1 ? sortedUnits[1] : false;
      const highestPricedUnit = sortedUnits.length > 2 ? sortedUnits[2] : false;

      return {
        hasVacantUnits: true,
        lowestPricedUnit,
        middlePricedUnit,
        highestPricedUnit,
      };
    };

    const floorplanResultsData = findFloorplanUnits(
      units,
      data.contentfulFloorplans.floorplanName
    );
    setFloorplanResults(floorplanResultsData);
  }, [units, data.contentfulFloorplans]);

  let images = [];
  if (data.contentfulFloorplans.floorplanImages) {
    let icount = 0;
    data.contentfulFloorplans.floorplanImages.forEach((i) => {
      images.push({ img: getImage(i), title: i.title, icount });
      icount += 1;
    });
  }

  const mapInSlices = (array, sliceSize, sliceFunc) => {
    const out = [];
    for (let i = 0; i < array.length; i += sliceSize) {
      const slice = array.slice(i, i + sliceSize);
      out.push(sliceFunc(slice, i));
    }
    return out;
  };

  const matterportUrl =
    data.contentfulFloorplans.matterportTour &&
    data.contentfulFloorplans.matterportTour.length > 0 &&
    data.contentfulFloorplans.matterportTour !== "none"
      ? data.contentfulFloorplans.matterportTour
      : false;

  const moreMatterportUrls =
    data.contentfulFloorplans.additionalMatterports &&
    data.contentfulFloorplans.additionalMatterports.length > 0
      ? data.contentfulFloorplans.additionalMatterports
      : false;

  // -----------------------------
  // NEW: Parse floorplanVideo (YouTube watch -> embed)
  // -----------------------------
  let floorplanVideo =
    data.contentfulFloorplans.galleryVideo &&
    data.contentfulFloorplans.galleryVideo.length > 0 &&
    data.contentfulFloorplans.galleryVideo.toLowerCase() !== "none"
      ? data.contentfulFloorplans.galleryVideo
      : false;

  if (floorplanVideo && floorplanVideo.includes("watch?v=")) {
    const videoId = floorplanVideo.split("watch?v=")[1];
    floorplanVideo = `https://www.youtube.com/embed/${videoId}`;
  }
  // -----------------------------

  const formatTitle = (title) => {
    let formattedTitle = title
      .replace(/Bed/g, "Bedroom")
      .replace(/Bath/g, "Bathroom");

    formattedTitle = formattedTitle.replace(/\+/g, (match) => {
      return `<span class="plus-symbol">${match}</span>`;
    });

    if (isMobile) {
      const parts = formattedTitle.split(" - ");
      if (parts.length === 3) {
        formattedTitle = `<span class="mobile-floorplan-title">${parts[0]}</span><br />${parts[1]}<br />${parts[2]}`;
      } else if (parts.length === 4) {
        formattedTitle = `<span class="mobile-floorplan-title">${parts[0]}</span><br />${parts[1]}<br />${parts[2]}<br />${parts[3]}`;
      }
    }
    return formattedTitle;
  };

  function formatCityName(cityName) {
    return cityName
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const hoursData = data.allContentfulHours.edges ?? [];
  const communityHoursDataArray = hoursData
    .filter(
      ({ node }) =>
        node.community &&
        node.community.some(
          (community) => community.slug === data.contentfulCommunity.slug
        ) &&
        node.active
    )
    .sort((a, b) => b.node.priority - a.node.priority);

  const communityHoursData =
    communityHoursDataArray.length > 0 ? communityHoursDataArray[0] : null;
//console.log(deals);
  return (
    <main>
      <CommunityHeader
        cname={data.contentfulCommunity.communityName}
        phone={data.contentfulCommunity.phone}
        address={data.contentfulCommunity.address}
        slug={data.contentfulCommunity.slug}
        area={data.contentfulCommunity.area}
        citySlug={data.contentfulCommunity.citySlug}
        actOveride={true}
        hours={data.contentfulCommunity.hours.hours}
        hoursData={communityHoursData ? communityHoursData.node.hours : null}
      />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <div className=" container" id="indiv_flrpln_page">
        <div className="columns is-mobile">
          <div className="column ">
            {!isMobile && (
              <h1
                className={`${fontSizeFive} ${fontColorBlue}`}
                style={{
                  textAlign: "center",
                  fontFamily: "MoonTime",
                  position: "relative",
                }}
                dangerouslySetInnerHTML={{
                  __html: formatTitle(data.contentfulFloorplans.floorplanTitle),
                }}
              ></h1>
            )}
            {isMobile && (
              <h1
                className={fontColorBlue}
                style={{ textAlign: "center" }}
                dangerouslySetInnerHTML={{
                  __html: formatTitle(data.contentfulFloorplans.floorplanTitle),
                }}
              ></h1>
            )}

            <p style={{ textAlign: "center" }} className={fontColorBlue}>
              No Extra Hidden Monthly Fees!
            </p>

            <br />
            {!isMobile &&
              data.contentfulFloorplans.floorplanDescription &&
              data.contentfulFloorplans.floorplanDescription && (
                <p
                  style={{ padding: "0 1rem 2rem", textAlign: "center" }}
                  className={fontColorDark}
                >
                  {
                    data.contentfulFloorplans.floorplanDescription
                      .floorplanDescription
                  }
                </p>
              )}
            {!loading && (
              <UnitDisplayCards
                floorplanResults={floorplanResults}
                slug={data.contentfulFloorplans.communitySlug}
                utilitiesIncluded={
                  data.contentfulCommunity.utilitiesIncluded ||
                  data.contentfulFloorplans.utilitiesIncluded
                }
                deals={deals}
                showDollhouse={showDollhouse}
                dollhouse={
                  data.contentfulFloorplans.bannerImage
                    ? data.contentfulFloorplans.bannerImage.gatsbyImageData
                    : ""
                }
                calloutFieldText={data.contentfulFloorplans.calloutField}
                communityName={data.contentfulCommunity.communityName}
              />
            )}
            <br />
            <p style={{ textAlign: "center" }}>
              {data.contentfulCommunity.communityName} Apartments in{" "}
              {formatCityName(data.contentfulCommunity.citySlug)},{" "}
              {data.contentfulCommunity.area}
            </p>
            {isMobile &&
              data.contentfulFloorplans.floorplanDescription &&
              data.contentfulFloorplans.floorplanDescription && (
                <p
                  style={{ padding: "0 1rem 2rem", textAlign: "center" }}
                  className={fontColorDark}
                >
                  {
                    data.contentfulFloorplans.floorplanDescription
                      .floorplanDescription
                  }
                </p>
              )}
            {avCount === 0 && (
              <div>
                <h3>No available units, please contact us:</h3>
                <ContactForm
                  toEmail={data.contentfulCommunity.email}
                  appendToBody={data.contentfulFloorplans.floorplanTitle}
                  uid={`floorplan-contact-form no-available ${data.contentfulFloorplans.floorplanSlug}`}
                />
              </div>
            )}

            {avCount > 0 && (
              <div>
                <br />
                <br />
                <button onClick={handleShowContactForm} className="button">
                  Contact
                </button>
                {showContactForm && (
                  <ContactForm
                    toEmail={data.contentfulCommunity.email}
                    uid={`floorplan-contact-form ${data.contentfulFloorplans.floorplanSlug}`}
                    appendToBody={data.contentfulFloorplans.floorplanTitle}
                  />
                )}
              </div>
            )}
            <br />
            <hr />
            {matterportUrl && matterportUrl !== "None" && (
              <div
                style={{ width: "100%", height: "500px", position: "relative" }}
              >
                <iframe
                  src={matterportUrl}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            )}
            {moreMatterportUrls &&
              moreMatterportUrls.map((murl) => (
                <div
                  key={murl}
                  style={{
                    width: "100%",
                    height: "500px",
                    position: "relative",
                    marginTop: "1rem",
                  }}
                >
                  <iframe
                    src={murl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              ))}
            <br />
            {mapInSlices(images, 3, (imageSlice, sliceIndex) => (
              <div
                className="columns"
                key={uuidv4()}
                style={{ marginBottom: "10px" }}
              >
                {imageSlice.map((img) => {
                  return (
                    <div
                      className={`${hoverTitleWrap} column is-one-third`}
                      key={img.icount}
                      style={{
                        maxHeight: "288px",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        marginBottom: "20px",
                      }}
                    >
                      <img
                        src={img.img.images.fallback.src}
                        alt={img.title}
                        onClick={() => {
                          setLightboxOpen(true);
                          setCurrentImage(img.icount);
                        }}
                      />
                      <p className={hoverTitle} id="floorplan_image_hover_title">
                        {img.title}
                      </p>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* 
              NEW: Render the YouTube video if present 
            */}
            {floorplanVideo && (
              <div
                style={{
                  width: "100%",
                  height: "500px",
                  position: "relative",
                  marginBottom: "2rem",
                  marginTop: "2rem",
                }}
              >
                <iframe
                  src={floorplanVideo}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allowFullScreen
                  loading="lazy"
                  title="Floorplan Video"
                ></iframe>
              </div>
            )}
          </div>
        </div>
        <a
          href={`/apartments/${data.contentfulCommunity.area.toLowerCase()}/${data.contentfulCommunity.citySlug.toLowerCase()}/${data.contentfulCommunity.slug.toLowerCase()}/floorplans/`}
        >
          &lt; back to floorplans
        </a>
      </div>
      <br />
      <br />
      <SlugWrapCommunityFooter slug={data.contentfulFloorplans.communitySlug} />
      {lightboxOpen && (
        <Lightbox
          mainSrc={images[currentImage].img.images.fallback.src}
          nextSrc={
            images[(currentImage + 1) % images.length].img.images.fallback.src
          }
          prevSrc={
            images[(currentImage + images.length - 1) % images.length].img.images
              .fallback.src
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setCurrentImage(
              (currentImage + images.length - 1) % images.length
            )
          }
          onMoveNextRequest={() =>
            setCurrentImage((currentImage + 1) % images.length)
          }
        />
      )}
    </main>
  );
};

export const data = graphql`
  query ($communitySlug: String, $floorplanSlug: String) {
    contentfulFloorplans(floorplanSlug: { eq: $floorplanSlug }) {
      id
      communitySlug
      floorplanImages {
        gatsbyImageData
        title
      }
      baths
      numBaths
      beds
      floorplanName
      floorplanTitle
      floorplanSlug
      matterportTour
      additionalMatterports
      utilitiesIncluded
      bannerImage {
        gatsbyImageData
      }
      floorplanDescription {
        floorplanDescription
      }
      seoDescription {
        seoDescription
      }
      unitOrderOverride
      galleryVideo
      calloutField
    }
    contentfulCommunity(slug: { eq: $communitySlug }) {
      id
      address
      area
      citySlug
      phone
      email
      communityName
      description
      seoDescription {
        seoDescription
      }
      slug
      hours {
        hours
      }
      shortHours
      rmpropid
      utilitiesIncluded
    }
    allContentfulHours {
      edges {
        node {
          hoursType
          active
          priority
          hours
          community {
            communityName
            slug
          }
        }
      }
    }
  }
`;

export const Head = ({ data }) => (
  <Seo
    title={`${data.contentfulFloorplans.floorplanName}
for ${data.contentfulCommunity.communityName} apartments in
${extractCityAndState(data.contentfulCommunity.address)} |
${data.contentfulFloorplans.beds} bedrooms ${
      data.contentfulFloorplans.baths
        ? data.contentfulFloorplans.baths
        : data.contentfulFloorplans.numBaths
    } bath Floorplans`}
    description={
      data.contentfulCommunity.seoDescription
        ? data.contentfulCommunity.seoDescription.seoDescription
        : data.contentfulCommunity.description
    }
  />
);

export default FloorplanPage;
